/**
 * Recent-alerts client buffer — a Zustand store of the most-recent
 * SAPIENT Alert rows, the single source feeding the Alerts panel
 * (REQ-UI-062) and (PR5b) the Map alert-pin layer. The Alert sibling of
 * the Detections buffer (:file:`detections/detections-store.ts`) under
 * the ADR-032 / ADR-029 cascade.
 *
 * # Why a store (not per-panel fetches)
 *
 * Per ADR-024 §5 + ADR-029 §6 the Map alert pins and the Alerts panel
 * show the *same* alert set with synchronized selection; and the live
 * ``alert-dismissed`` SSE fan-out (ADR-029 [MVP] item 6) arrives at every
 * session and must drop the alert from *every* surface at once. A
 * module-level Zustand store (mirroring
 * :file:`detections/detections-store.ts`) is the smallest tool that
 * scales — both surfaces subscribe with narrow selectors, and a single
 * :func:`AlertsState.dismiss` write propagates everywhere.
 *
 * # Three feeders, one buffer
 *
 *   - **REST back-fill** — :func:`alerts/use-recent-alerts-feed` seeds and
 *     periodically refreshes the buffer from
 *     ``GET /api/query/recent-alerts?active_only=true`` (global, active
 *     window). This is the authoritative active-alert list.
 *   - **Live SSE merge** — :func:`alerts/use-alerts-sse-bridge` upserts the
 *     live ``alert``-kind SSE deltas and, on the ``alert-dismissed``
 *     fan-out, drops the dismissed alert from the active view.
 *   - **Dismiss mutation** — :func:`alerts/use-dismiss-alert` optimistically
 *     marks the alert dismissed on success (the ``alert-dismissed`` SSE
 *     follows and is idempotent — see :func:`AlertsState.dismiss`).
 *
 * # Dedup key — ``alert_id``
 *
 * Dedup is by ``alert_id`` (the BSI per-alert ULID; the PK + dismissal +
 * detail-lookup key) so all three feeders converge on one row.
 *
 * # Dismissal semantics — mark, don't delete
 *
 * :func:`AlertsState.dismiss` *marks* the alert dismissed
 * (``dismissed_at``/``dismissed_by`` set) rather than deleting the row.
 * The active selector (:func:`useActiveAlerts`) filters
 * ``dismissed_at === null`` so a dismissed alert leaves the panel
 * immediately, but the record survives for :func:`useAlertById` (the
 * detail surface / history) and — crucially — for **idempotency**: the
 * dismiss-mutation success path and the cross-session ``alert-dismissed``
 * SSE both call ``dismiss(alert_id)``; marking an already-marked alert is
 * a harmless no-op, where a delete-then-re-dismiss would race.
 *
 * # Retention
 *
 * Alerts are pruned on every ``upsert`` once their ``received_at`` ages
 * past :data:`ALERT_RETENTION_MS` (10 min — mirrors the Detections
 * buffer's window). A safety :data:`ALERT_BUFFER_CAP` bounds memory under
 * burst; the newest are kept. Idle staleness is bounded by the REST
 * feed's refetch cadence (each refetch upserts and therefore prunes).
 *
 * Cross-references:
 *   - REQ-UI-027 (Zustand for client state).
 *   - REQ-UI-062 (Alerts panel).
 *   - REQ-QUERY-025 (recent + dismiss Alert surfaces).
 *   - REQ-INGEST-044 (nullable position).
 *   - ADR-024 §2 / §5 (Alerts panel + selection contract).
 *   - ADR-029 §4 (dismissal columns) / §6 (Alert panel) / [MVP] item 6
 *     (cross-session dismissal fan-out).
 *   - ADR-032 §4.2 (Alert data model).
 */
import { useMemo } from "react";
import { create } from "zustand";

/**
 * Alerts age out 10 min after receipt (mirrors the Detections buffer's
 * window). The buffer prunes to this window so the panel — and (PR5b) the
 * map — both reflect "the last 10 min of active alerts".
 */
export const ALERT_RETENTION_MS = 10 * 60 * 1000;

/**
 * Safety bound on buffer size. The global REST feed caps its result set
 * and the live SSE alert stream is a low rate, so the buffer is normally
 * far below this; the cap only guards a pathological burst. When
 * exceeded, the newest alerts (by ``received_at``) are kept.
 */
export const ALERT_BUFFER_CAP = 500;

/**
 * The normalized client shape for a SAPIENT alert — the fields the Alerts
 * panel rows, the (PR5b) Map alert-pin layer, and the selection edge
 * need. All three feeders map their wire shape (``AlertItem`` /
 * ``SseAlertPayload`` / the ``SseAlertDismissedPayload`` partial) into (or
 * onto) this; Entity Details fetches the full record by ``alert_id`` via
 * the detail endpoint when an alert is selected (PR5a inc 4).
 *
 * **Position is nullable** (REQ-INGEST-044): an unprojectable BSI
 * range/bearing yields ``null`` lat/lng/elev — a *normal* state for an
 * alert (the map pins it only when the location resolves, REQ-UI-069).
 *
 * **Dismissal columns** (``dismissed_at`` / ``dismissed_by`` /
 * ``dismissal_note``) are ``null`` while the alert is active; the active
 * selector keys off ``dismissed_at``.
 */
export interface AlertRecord {
  /** BSI alert_id ULID — PK + dismissal key + selection-bus id for ``kind: "alert"`` + dedup key. */
  readonly alert_id: string;
  /** Source Producer. */
  readonly producer_id: string;
  /** Integrating Aggregator that raised/multiplexed the alert. */
  readonly aggregator_id: string;
  /** Connector-receipt timestamp (the sort/list dimension), ISO-8601 UTC string. */
  readonly received_at: string;
  /** SAPIENT ``AlertType`` enum integer value. */
  readonly alert_type: number;
  /** SAPIENT ``AlertStatus`` enum integer value. */
  readonly status: number;
  /** SAPIENT ``AlertPriority`` enum integer value — drives the priority badge + Flow-F surface (ADR-029 §5). */
  readonly priority: number;
  /** Operator-readable description; ``null`` when the source omitted it. */
  readonly description: string | null;
  /** Associated BSI region identifier; ``null`` when absent. */
  readonly region_id: string | null;
  /** BSI ranking in [0, 1]; ``null`` when absent. */
  readonly ranking: number | null;
  /** BSI confidence in [0, 1]; ``null`` when absent. */
  readonly confidence: number | null;
  /** WGS84 latitude in decimal degrees; ``null`` when unprojectable. */
  readonly latitude_deg: number | null;
  /** WGS84 longitude in decimal degrees; ``null`` when unprojectable. */
  readonly longitude_deg: number | null;
  /** WGS84 elevation in metres; ``null`` when unprojectable. */
  readonly elevation_m: number | null;
  /** Schema version for the source row. */
  readonly schema_version: number;
  /** When the alert was dismissed, ISO-8601 UTC string; ``null`` while active. */
  readonly dismissed_at: string | null;
  /** Operator user_id who dismissed the alert; ``null`` while active. */
  readonly dismissed_by: string | null;
  /** Optional operator-supplied dismissal note; ``null`` when none / while active. */
  readonly dismissal_note: string | null;
  /** Source aggregator identifier; empty string for direct Producers. */
  readonly source_aggregator_id: string;
  /** When the connector accepted the bytes off the wire, ISO-8601 UTC string. */
  readonly ingress_timestamp: string;
  /** SHA-256 of the source payload, lowercase-hex-encoded (64 chars). */
  readonly message_hash: string;
  /** CDS originating-network label when arrived via the Cross Domain Solution; ``null`` when locally-ingested. */
  readonly source_network: string | null;
}

/** Optional knobs for {@link AlertsState.upsert}. */
export interface UpsertOptions {
  /**
   * The reference "now" (epoch ms) used for age-based pruning. Defaults
   * to ``Date.now()``; tests pass an explicit value for determinism.
   */
  readonly now?: number;
}

/** Details carried by an {@link AlertsState.dismiss} call. */
export interface DismissDetails {
  /** Dismissal timestamp, ISO-8601 UTC string. Defaults to ``new Date().toISOString()``. */
  readonly dismissedAt?: string;
  /** Username of the dismissing operator. Defaults to ``null``. */
  readonly dismissedBy?: string | null;
  /** Optional operator-supplied dismissal note. Defaults to ``null``. */
  readonly dismissalNote?: string | null;
}

/** The alerts store shape. */
export interface AlertsState {
  /** Alerts keyed by ``alert_id`` (dedup across the feeders). */
  readonly byId: Record<string, AlertRecord>;
  /**
   * Merge alerts into the buffer, then prune by age + cap by size.
   * Last-writer-wins per ``alert_id``.
   */
  readonly upsert: (alerts: readonly AlertRecord[], opts?: UpsertOptions) => void;
  /**
   * Mark the alert with ``alertId`` dismissed so the active selector drops
   * it. Idempotent: a no-op if the alert is unknown or already dismissed
   * (so the dismiss-mutation success path and the cross-session
   * ``alert-dismissed`` SSE converge without racing — see module
   * docstring). The record is *retained* (marked, not deleted) for the
   * detail surface + history.
   */
  readonly dismiss: (alertId: string, details?: DismissDetails) => void;
  /** Empty the buffer (e.g., on sign-out / network switch). */
  readonly clear: () => void;
}

/**
 * The module-level alerts store. Consume via the narrow-subscription
 * helpers (:func:`useActiveAlerts`, :func:`useAlertById`) rather than
 * calling as a bare hook. Non-React writers (the SSE bridge effect, the
 * dismiss mutation's ``onSuccess``) use
 * ``useAlertsStore.getState().upsert(...)`` / ``.dismiss(...)``.
 */
export const useAlertsStore = create<AlertsState>((set) => ({
  byId: {},
  upsert: (alerts, opts) =>
    set((state) => {
      const now = opts?.now ?? Date.now();
      const floor = now - ALERT_RETENTION_MS;
      const next: Record<string, AlertRecord> = { ...state.byId };
      for (const alert of alerts) {
        next[alert.alert_id] = alert;
      }
      // Prune by alert age (mirrors the Detections buffer's 10-min window).
      for (const id of Object.keys(next)) {
        if (Date.parse(next[id].received_at) < floor) {
          delete next[id];
        }
      }
      // Safety cap: keep the newest ALERT_BUFFER_CAP by received_at.
      const ids = Object.keys(next);
      if (ids.length > ALERT_BUFFER_CAP) {
        ids.sort((a, b) => Date.parse(next[b].received_at) - Date.parse(next[a].received_at));
        for (const id of ids.slice(ALERT_BUFFER_CAP)) {
          delete next[id];
        }
      }
      return { byId: next };
    }),
  dismiss: (alertId, details) =>
    set((state) => {
      const existing = state.byId[alertId];
      // Idempotent: unknown alert OR already-dismissed → no state change
      // (preserve the reference so subscribers don't re-render).
      if (existing === undefined || existing.dismissed_at !== null) {
        return state;
      }
      return {
        byId: {
          ...state.byId,
          [alertId]: {
            ...existing,
            dismissed_at: details?.dismissedAt ?? new Date().toISOString(),
            dismissed_by: details?.dismissedBy ?? null,
            dismissal_note: details?.dismissalNote ?? null,
          },
        },
      };
    }),
  clear: () => {
    set({ byId: {} });
  },
}));

// ---- Narrow-subscription helpers ----------------------------------

/**
 * The active (undismissed) alerts as a newest-first array (by
 * ``received_at``), per ADR-029 §6.2.
 *
 * Subscribes to ``byId`` (a stable reference between mutations) and
 * derives the filtered-and-sorted array via ``useMemo`` keyed on that
 * reference — so the returned array is referentially stable until the
 * buffer actually changes, avoiding render churn in the Alerts panel.
 */
export function useActiveAlerts(): AlertRecord[] {
  const byId = useAlertsStore((state) => state.byId);
  return useMemo(
    () =>
      Object.values(byId)
        .filter((a) => a.dismissed_at === null)
        .sort((a, b) => Date.parse(b.received_at) - Date.parse(a.received_at)),
    [byId],
  );
}

/**
 * The buffered alert with ``alertId``, or ``null`` if absent. Returns the
 * record regardless of dismissal state (the detail surface shows
 * who/when/why for a dismissed alert). Used by the Alerts panel for row
 * highlighting and as a cheap cache the (PR5b) Map pin layer can read;
 * Entity Details fetches the authoritative record by id via the detail
 * endpoint (PR5a inc 4).
 */
export function useAlertById(alertId: string): AlertRecord | null {
  return useAlertsStore((state) => state.byId[alertId] ?? null);
}
